// Shared field-list builder: add / reorder / configure / delete the fields on a
// flow component. Extracted from FormComponents/index.tsx so the FORM step and
// the INSTRUCTION step configure fields through the same UI instead of two
// drifting copies.
//
// Owns only the field list. Step-level concerns (title, status, notification,
// escalation, step-type switching) stay with the host form.
import { useState, type ReactNode } from 'react';
import { Box, Button, Popover } from '@mui/material';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { SortableContext, useSortable, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useParams } from 'react-router-dom';
import { MenuItem, Select } from '@mui/material';
import InputComponent from './Components/InputComponent';
import CheckboxComponent from './Components/CheckboxComponents';
import SelectComponent from './Components/SelectComponent';
import Radiocomponent from './Components/RadioComponent';
import Numbercomponent from './Components/NumberComponent';
import FileComponent from './Components/FileComponent';
import CameraComponent from './Components/CameraComponents';
import DateComponents from './Components/DateComponents';
import EsignComponent from './Components/EsignComponent';
import SwitchComponent from './Components/SwitchComponent';
import ResourceComponent from './Components/ResourceComponent';
import DefaultForm from './DefaultForm';
import WarningModal from 'utils/warningModel/warningMdel';
import useApi from 'hooks/useApi';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';



export 
enum InputComponents {
  UNDEFINED = 'UNDEFINED',
  TEXT = 'TEXT',
  NUMBER = 'NUMBER',
  FILE = 'FILE',
  // PASSWORD = 'PASSWORD',
  SELECT = 'SELECT',
  // DROPDOWN = 'DROPDOWN',
  CHECKBOX = 'CHECKBOX',

  RADIO = 'RADIO',
  CAMERA = 'CAMERA',
  FORM = 'FORM',
  DATE = 'DATE',
  ESIGN = 'ESIGN',
  // TEXTARRAY = 'TEXTARRAY',
  SWITCH = 'SWITCH',
  RESOURCE = 'RESOURCE'
}

const FIELD_TYPE_OPTIONS = [
  { value: InputComponents.TEXT, label: 'TEXT' },
  { value: InputComponents.NUMBER, label: 'NUMBER' },
  { value: InputComponents.FILE, label: 'FILE' },
  { value: InputComponents.SELECT, label: 'DROPDOWN' },
  { value: InputComponents.CHECKBOX, label: 'CHECKBOX' },
  { value: InputComponents.RADIO, label: 'RADIO' },
  { value: InputComponents.CAMERA, label: 'CAMERA' },
  { value: InputComponents.DATE, label: 'DATE' },
  { value: InputComponents.ESIGN, label: 'ESIGN' },
  { value: InputComponents.SWITCH, label: 'TOGGLE' },
  { value: InputComponents.RESOURCE, label: 'RESOURCE' }
];

const FieldTypeSelector = ({
  value,
  onChange
}: {
  value: InputComponents;
  onChange: (value: InputComponents) => void;
}) => (
  <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1} mb={1}>
    <Select
      size="small"
      value={value}
      onChange={(event) => onChange(event.target.value as InputComponents)}
      sx={{ minWidth: 160 }}
    >
      {FIELD_TYPE_OPTIONS.map((option) => (
        <MenuItem key={option.value} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
    </Select>
  </Box>
);

export interface Field {
  question: string;
  description: string;
  inputComponent: InputComponents;
  options: string[];
  cameraData?: any;
  resourceType?: string;
  settings?: {
    required?: boolean;
    multiple?: boolean;
    isSupportFileRequired?: boolean;
    noOfSupportImages?: number;
    [key: string]: any;
  };
}

type SortableFieldItemProps = {
  id: string;
  children: ReactNode;
};

const SortableFieldItem = ({ id, children }: SortableFieldItemProps) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.7 : 1
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        px: 3
      }}
    >
      <Box display="flex" alignItems="center" gap={1}>
        <Box
          ref={setActivatorNodeRef}
          {...attributes}
          {...listeners}
          aria-label="Drag to reorder"
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: isDragging ? 'grabbing' : 'grab',
            touchAction: 'none',
            color: 'text.disabled'
          }}
        >
          <DragIndicatorIcon sx={{ color: 'gray' }} />
        </Box>
        <Box flex={1}>{children}</Box>
      </Box>
    </Box>
  );
};


const getFieldUid = (
  field: any,
  fieldIndex: number,
  selectedNode: any,
  selectedNodeData: any
): string | null => {
    const directUid = extractUidFromField(field);
    if (directUid) {
      return directUid;
    }

    const sourceFields = selectedNode?.data?.step?.component?.fields || selectedNodeData?.fields || [];
    const byIndexField = sourceFields[fieldIndex];
    const byIndexUid = extractUidFromField(byIndexField);
    if (byIndexUid) {
      return byIndexUid;
    }

    const byQuestionField = sourceFields.find((sourceField: any) =>
      sourceField?.question === field?.question && sourceField?.inputComponent === field?.inputComponent
    );

    return extractUidFromField(byQuestionField);
  };

const getUsageNames = (section: any): string[] => {
    if (!section) return [];

    const items = Array.isArray(section?.items)
      ? section.items
      : Array.isArray(section)
      ? section
      : [];

    return items
      .map((item: any) => item?.title || item?.name || item?.uid)
      .filter(Boolean);
  };

const buildDependencyWarningSubHeadings = (dependencyData: any): string[] => {
    const usage = dependencyData?.usage || {};
    const rules = getUsageNames(usage.rules);
    const notifications = getUsageNames(usage.notifications);
    const alerts = getUsageNames(usage.alerts);
    const priority = getUsageNames(usage.priority);

    const details: string[] = [];

    const pushNumbered = (title: string, items: string[]) => {
      if (!items.length) return;
      details.push(`${title}:`);
      items.forEach((item, index) => {
        details.push(`${index + 1}. ${item}`);
      });
    };

    pushNumbered('Rules', rules);
    pushNumbered('Notifications', notifications);
    pushNumbered('Alerts', alerts);
    pushNumbered('Priority', priority);

    if (!details.length && dependencyData?.message) {
      details.push(dependencyData.message);
    }

    return details;
  };

const extractUidFromField = (field: any): string | null => {
    if (!field) return null;

    const candidates = [
      field?.uid,
      field?._id,
      field?.id,
      field?.fieldUid,
      typeof field?.field === 'string' ? field.field : field?.field?.uid || field?.field?._id || field?.field?.id
    ];

    for (const candidate of candidates) {
      if (candidate && typeof candidate === 'string') {
        return candidate;
      }
    }

    return null;
  };

// Declared at module scope on purpose. Defining a component inside another
// component's body gives it a new identity on every render, so React unmounts
// and remounts the whole subtree — which reset ResourceComponent's
// "already fetched" guard and made it refetch resource types in a loop.
const RenderFieldComponent = ({
  values,
  setFieldValue,
  index,
  handleChange,
  handleBlur,
  selectedNodeData,
  selectedNode,
  onSubmit,
  clientUID,
  teamUid,
  checkDeleteDependencies,
  openFieldSettingsIndex,
  handleFieldSettingsOpen,
  handleFieldSettingsClose,
  setDependencyWarningHeading,
  setDependencyWarningSubHeadings,
  setDependencyWarningHasRules,
  setDependencyWarningMessage,
  setPendingDeleteFieldIndex,
  setDependencyWarningOpen
}: {
  values: any;
  index: number;
  setFieldValue: any;
  handleChange: any;
  handleBlur: any;
  selectedNodeData: any;
  selectedNode?: any;
  onSubmit: (data: any) => void;
  clientUID?: string;
  teamUid?: string;
  checkDeleteDependencies: any;
  openFieldSettingsIndex: number | null;
  handleFieldSettingsOpen: (index: number) => void;
  handleFieldSettingsClose: () => void;
  setDependencyWarningHeading: (v: string) => void;
  setDependencyWarningSubHeadings: (v: string[]) => void;
  setDependencyWarningHasRules: (v: boolean) => void;
  setDependencyWarningMessage: (v: string) => void;
  setPendingDeleteFieldIndex: (v: number | null) => void;
  setDependencyWarningOpen: (v: boolean) => void;
}) => {
    const handlelinputhandle = (data: any) => {
      console.log('Received from child input--text:', data);
    };

    // Add safety check for field existence
    const field = values?.fields?.[index];
    if (!field) {
      console.warn(`Field at index ${index} not found during render`);
      return null;
    }

    const getFieldLabel = (currentField: any): string => {
      if (!currentField) return '';
      if (typeof currentField.question === 'string' && currentField.question.trim()) {
        return currentField.question;
      }
      if (typeof currentField.label === 'string' && currentField.label.trim()) {
        return currentField.label;
      }
      return '';
    };

    const buildResetField = (currentField: any, nextType: InputComponents) => {
      const label = getFieldLabel(currentField);

      return {
        uid: currentField?.uid,
        _id: currentField?._id,
        id: currentField?.id,
        fieldUid: currentField?.fieldUid,
        question: label,
        label,
        description: '',
        inputComponent: nextType,
        options: [],
        resourceType: nextType === InputComponents.RESOURCE ? '' : undefined,
        settings: nextType === InputComponents.RESOURCE ? { multiple: true } : {}
      };
    };

    const handleFieldTypeChange = (nextType: InputComponents) => {
      const currentType = field.inputComponent || InputComponents.UNDEFINED;
      if (currentType === nextType) {
        return;
      }

      const nextField = buildResetField(field, nextType);
      setFieldValue(`fields.${index}`, nextField);
    };

    const isSettingsOpen = openFieldSettingsIndex === index;

    const fieldTypeSelector = (
      <FieldTypeSelector
        value={(field.inputComponent as InputComponents) || InputComponents.TEXT}
        onChange={handleFieldTypeChange}
      />
    );
    
    function toSentenceCase(input: string): string {
      const quotedMap: Record<number, string> = {};
      let idx = 0;

      // Step 1: Replace "quoted" text with lowercase placeholders
      //         Store the UPPERCASED version in quotedMap
      const withPlaceholders = input.replace(
        /"([^"]*)"/g,
        (_: string, inner: string): string => {
          const key = `__q${idx}__`;
          quotedMap[idx] = inner.toUpperCase();
          idx++;
          return key;
        }
      );

      // Step 2: Lowercase everything, then capitalize only the first char
      const sentenceCased = withPlaceholders
        .toLowerCase()
        .replace(/^\w/, (c: string): string => c.toUpperCase());

      // Step 3: Restore placeholders with their UPPERCASE values
      return sentenceCased.replace(
        /__q(\d+)__/g,
        (_: string, i: string): string => quotedMap[Number(i)]
      );
    }

    // Safe deletion handler with immediate state update
    const handleDeleteField = async (fieldIndex: number) => {
      console.log('🗑️ Deleting field at index:', fieldIndex, 'Total fields:', values.fields.length);
      
      // Ensure we don't delete if index is out of bounds
      if (fieldIndex < 0 || fieldIndex >= values.fields.length) {
        console.warn('Invalid field index for deletion:', fieldIndex);
        return;
      }

      const fieldToDelete = values.fields[fieldIndex];
      const fieldUid = getFieldUid(fieldToDelete, fieldIndex, selectedNode, selectedNodeData);
      const deleteFieldLocally = () => {
        const updatedFields = values.fields.filter((_: any, i: number) => i !== fieldIndex);
        setFieldValue('fields', updatedFields);
        console.log('🔄 Fields after deletion:', updatedFields.length);
      };

      setDependencyWarningHasRules(false);
      setDependencyWarningMessage('');

      // For unsaved/new fields (no uid) or missing context, preserve existing behavior.
      if (!teamUid || !fieldUid) {
        deleteFieldLocally();
        return;
      }

      try {
        const dependencyResponse = await checkDeleteDependencies(teamUid, { fieldUid });
        const dependencyData = dependencyResponse?.data;

        if (dependencyData?.canDelete) {
          deleteFieldLocally();
          return;
        }

        const ruleNames = getUsageNames(dependencyData?.usage?.rules);
        const hasRules = ruleNames.length > 0;

        const fieldName = dependencyData?.resolved?.fieldQuestion || fieldToDelete?.question || 'This field';
        setDependencyWarningHeading(`"${fieldName}" Want to delete this field ?`);
        setDependencyWarningSubHeadings(buildDependencyWarningSubHeadings(dependencyData));
        setDependencyWarningHasRules(hasRules);
        setDependencyWarningMessage(
          hasRules
            ? 'This field is associated with rule(s). Delete the rule first, then you can delete this field.'
            : 'This field is associated with existing configurations.'
        );
        setPendingDeleteFieldIndex(fieldIndex);
        setDependencyWarningOpen(true);
        return;
      } catch (error) {
        console.error('Dependency check failed, proceeding with local deletion:', error);
      }
      
      deleteFieldLocally();
    };

    const currentType = (field.inputComponent as InputComponents) || InputComponents.TEXT;
    switch (currentType) {
      case InputComponents.TEXT:
        return <InputComponent 
          {...{ values, setFieldValue, index, handleChange, handleBlur }} 
          onSubmit={handlelinputhandle}  
          selectedNodeData={selectedNodeData} 
          onDeleteField={handleDeleteField}
          toSentenceCase={toSentenceCase}
          fieldTypeSelector={fieldTypeSelector}
          isSettingsOpen={isSettingsOpen}
          onSettingsOpen={handleFieldSettingsOpen}
          onSettingsClose={handleFieldSettingsClose}
        />;

      case InputComponents.NUMBER:
        return <Numbercomponent 
          {...{ values, setFieldValue, index, handleChange, handleBlur, selectedNodeData }} 
          onSubmit={handlelinputhandle} 
          onDeleteField={handleDeleteField}
          toSentenceCase={toSentenceCase}
          fieldTypeSelector={fieldTypeSelector}
          isSettingsOpen={isSettingsOpen}
          onSettingsOpen={handleFieldSettingsOpen}
          onSettingsClose={handleFieldSettingsClose}
        />;

      case InputComponents.FILE:
        return <FileComponent 
          {...{ values, setFieldValue, index, handleChange, handleBlur, selectedNodeData }} 
          onSubmit={handlelinputhandle} 
          onDeleteField={handleDeleteField}
          toSentenceCase={toSentenceCase}
          fieldTypeSelector={fieldTypeSelector}
          isSettingsOpen={isSettingsOpen}
          onSettingsOpen={handleFieldSettingsOpen}
          onSettingsClose={handleFieldSettingsClose}
        />;
      
      case InputComponents.CHECKBOX:
        return (
          <CheckboxComponent
            values={values}
            index={index}
            setFieldValue={setFieldValue}
            onSubmit={onSubmit}
            handleChange={handleChange}
            handleBlur={handleBlur}
            selectedNodeData={selectedNodeData}
            onDeleteField={handleDeleteField}
            toSentenceCase={toSentenceCase}
            fieldTypeSelector={fieldTypeSelector}
            isSettingsOpen={isSettingsOpen}
            onSettingsOpen={handleFieldSettingsOpen}
            onSettingsClose={handleFieldSettingsClose}
          />
        );
      case InputComponents.SELECT:
        return (
          <SelectComponent
            values={values}
            index={index}
            setFieldValue={setFieldValue}
            onSubmit={onSubmit}
            handleChange={handleChange}
            handleBlur={handleBlur}
            selectedNodeData={selectedNodeData}
            onDeleteField={handleDeleteField}
            toSentenceCase={toSentenceCase}
            fieldTypeSelector={fieldTypeSelector}
            isSettingsOpen={isSettingsOpen}
            onSettingsOpen={handleFieldSettingsOpen}
            onSettingsClose={handleFieldSettingsClose}
          />
        );
      case InputComponents.RADIO:
        return (
          <Radiocomponent
            values={values}
            index={index}
            setFieldValue={setFieldValue}
            onSubmit={onSubmit}
            handleChange={handleChange}
            handleBlur={handleBlur}
            selectedNodeData={selectedNodeData}
            onDeleteField={handleDeleteField}
            toSentenceCase={toSentenceCase}
            fieldTypeSelector={fieldTypeSelector}
            isSettingsOpen={isSettingsOpen}
            onSettingsOpen={handleFieldSettingsOpen}
            onSettingsClose={handleFieldSettingsClose}
          />
        );
      case InputComponents.CAMERA:
        return <CameraComponent 
          values={values} 
          index={index} 
          setFieldValue={setFieldValue}
          selectedNodeData={selectedNodeData}
          onDeleteField={handleDeleteField}
          toSentenceCase={toSentenceCase}
          fieldTypeSelector={fieldTypeSelector}
          isSettingsOpen={isSettingsOpen}
          onSettingsOpen={handleFieldSettingsOpen}
          onSettingsClose={handleFieldSettingsClose}
        />;
      case InputComponents.DATE:
        return <DateComponents 
          values={values} 
          index={index} 
          setFieldValue={setFieldValue}
          selectedNodeData={selectedNodeData}
          onDeleteField={handleDeleteField}
          toSentenceCase={toSentenceCase}
          fieldTypeSelector={fieldTypeSelector}
          isSettingsOpen={isSettingsOpen}
          onSettingsOpen={handleFieldSettingsOpen}
          onSettingsClose={handleFieldSettingsClose}
        />;
      case InputComponents.ESIGN:
        return <EsignComponent 
          values={values} 
          index={index} 
          setFieldValue={setFieldValue}
          selectedNodeData={selectedNodeData}
          onDeleteField={handleDeleteField}
          toSentenceCase={toSentenceCase}
          fieldTypeSelector={fieldTypeSelector}
          isSettingsOpen={isSettingsOpen}
          onSettingsOpen={handleFieldSettingsOpen}
          onSettingsClose={handleFieldSettingsClose}
        />;
      // case InputComponents.TEXTARRAY:
        // return <TextArraycomponent values={values} index={index} setFieldValue={setFieldValue} />;
      case InputComponents.SWITCH:
        return (
          <SwitchComponent
            values={values}
            index={index}
            setFieldValue={setFieldValue}
            onSubmit={handlelinputhandle}
            handleChange={handleChange}
            handleBlur={handleBlur}
            selectedNodeData={selectedNodeData}
            onDeleteField={handleDeleteField}
            toSentenceCase={toSentenceCase}
            fieldTypeSelector={fieldTypeSelector}
            isSettingsOpen={isSettingsOpen}
            onSettingsOpen={handleFieldSettingsOpen}
            onSettingsClose={handleFieldSettingsClose}
          />
        );
      case InputComponents.RESOURCE:
        return (
          <ResourceComponent
            values={values}
            index={index}
            setFieldValue={setFieldValue}
            onSubmit={handlelinputhandle}
            handleChange={handleChange}
            handleBlur={handleBlur}
            clientUID={clientUID}
            selectedNodeData={selectedNodeData}
            onDeleteField={handleDeleteField}
            toSentenceCase={toSentenceCase}
            fieldTypeSelector={fieldTypeSelector}
            isSettingsOpen={isSettingsOpen}
            onSettingsOpen={handleFieldSettingsOpen}
            onSettingsClose={handleFieldSettingsClose}
          />
        );
      default:
        return null;
    }
  };

export interface FieldListBuilderProps {
  /** Formik bag from the host form. `values.fields` is the array we manage. */
  values: any;
  setFieldValue: any;
  handleChange: any;
  handleBlur: any;
  selectedNodeData: any;
  /** The flow node, when the host has it — used to resolve saved field uids. */
  selectedNode?: any;
  /** Passed through to the per-type field editors, as the FORM builder does. */
  onSubmit?: (data: any) => void;
  /** Client uid for DefaultForm's resource-type picker. */
  clientUID?: string;
  /** Restrict the offered field types. Defaults to every type. */
  allowedTypes?: InputComponents[];
  /** Label on the add button. Defaults to the FORM builder's wording. */
  addLabel?: string;
}

const FieldListBuilder = ({
  values,
  setFieldValue,
  handleChange,
  handleBlur,
  selectedNodeData,
  selectedNode,
  onSubmit = () => {},
  clientUID,
  allowedTypes,
  addLabel = 'Add Field'
}: FieldListBuilderProps) => {
  const { teamUid } = useParams<{ teamUid: string }>();
  const { checkDeleteDependencies } = useApi();
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openFieldSettingsIndex, setOpenFieldSettingsIndex] = useState<number | null>(null);
  const [dependencyWarningOpen, setDependencyWarningOpen] = useState(false);
  const [dependencyWarningHeading, setDependencyWarningHeading] = useState('');
  const [dependencyWarningSubHeadings, setDependencyWarningSubHeadings] = useState<string[]>([]);
  const [pendingDeleteFieldIndex, setPendingDeleteFieldIndex] = useState<number | null>(null);
  const [dependencyWarningHasRules, setDependencyWarningHasRules] = useState(false);
  const [dependencyWarningMessage, setDependencyWarningMessage] = useState('');

  const openPopover = Boolean(anchorEl);
  const handleClose = () => setAnchorEl(null);
  const handleFieldSettingsOpen = (index: number) => setOpenFieldSettingsIndex(index);
  const handleFieldSettingsClose = () => setOpenFieldSettingsIndex(null);

  const fields = Array.isArray(values?.fields) ? values.fields : [];
  const fieldIds = fields.map((_: any, index: number) => `field-${index}`);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = fieldIds.indexOf(active.id as string);
    const newIndex = fieldIds.indexOf(over.id as string);
    if (oldIndex === -1 || newIndex === -1) return;
    setFieldValue('fields', arrayMove(fields, oldIndex, newIndex));
  };

  return (
    <Box>
      <Popover
        open={openPopover}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <DefaultForm
          onAddField={(data: any) => {
            const { type, label, resourceType } = data;
            const fieldData: any = {
              question: label,
              description: '',
              inputComponent: InputComponents[type as keyof typeof InputComponents],
              options: [],
              resourceType: type === 'RESOURCE' ? resourceType : undefined,
              settings: {}
            };
            if (type === 'RESOURCE') fieldData.settings.multiple = true;
            setFieldValue('fields', [...fields, fieldData]);
            handleClose();
          }}
          showFieldTypeSelector={true}
          clientUID={clientUID}
          allowedTypes={allowedTypes}
        />
      </Popover>

      <Box pt={3} mb={10}>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={fieldIds} strategy={verticalListSortingStrategy}>
            {fields.map((_: any, index: number) => (
              <SortableFieldItem key={fieldIds[index]} id={fieldIds[index]}>
                <RenderFieldComponent
                  {...{ values, setFieldValue, index, handleChange, handleBlur }}
                  selectedNodeData={selectedNodeData}
                  selectedNode={selectedNode}
                  onSubmit={onSubmit}
                  clientUID={clientUID}
                  teamUid={teamUid}
                  checkDeleteDependencies={checkDeleteDependencies}
                  openFieldSettingsIndex={openFieldSettingsIndex}
                  handleFieldSettingsOpen={handleFieldSettingsOpen}
                  handleFieldSettingsClose={handleFieldSettingsClose}
                  setDependencyWarningHeading={setDependencyWarningHeading}
                  setDependencyWarningSubHeadings={setDependencyWarningSubHeadings}
                  setDependencyWarningHasRules={setDependencyWarningHasRules}
                  setDependencyWarningMessage={setDependencyWarningMessage}
                  setPendingDeleteFieldIndex={setPendingDeleteFieldIndex}
                  setDependencyWarningOpen={setDependencyWarningOpen}
                />
              </SortableFieldItem>
            ))}
          </SortableContext>
        </DndContext>

        {/* Add Field Button - Always visible below last field */}
        <Box display="flex" justifyContent="flex-start" mt={2} px={3}>
          <Button
            variant="outlined"
            color="primary"
            onClick={(e) => setAnchorEl(e.currentTarget)}
            sx={{
              border: '2px solid #ccc',
              borderRadius: '8px',
              padding: '8px 8px'
            }}
          >
            {addLabel}
          </Button>
        </Box>
      </Box>

      <WarningModal
        open={dependencyWarningOpen}
        onClose={() => {
          setDependencyWarningOpen(false);
          setPendingDeleteFieldIndex(null);
          setDependencyWarningHasRules(false);
          setDependencyWarningMessage('');
        }}
        title="Deleting Field ?"
        type="warning"
        heading={dependencyWarningHeading || 'Are you Sure you want to delete this field ?'}
        message={dependencyWarningMessage || 'This field is associated with existing configurations.'}
        subHeadings={dependencyWarningSubHeadings}
        confirmText="Delete Field"
        cancelText="Cancel"
        onCancel={() => {
          setDependencyWarningOpen(false);
          setPendingDeleteFieldIndex(null);
          setDependencyWarningHasRules(false);
          setDependencyWarningMessage('');
        }}
        onConfirm={() => {
          if (dependencyWarningHasRules) return;
          if (pendingDeleteFieldIndex !== null) {
            setFieldValue('fields', fields.filter((_: any, i: number) => i !== pendingDeleteFieldIndex));
          }
          setDependencyWarningOpen(false);
          setPendingDeleteFieldIndex(null);
          setDependencyWarningHasRules(false);
          setDependencyWarningMessage('');
        }}
        showCancelButton={true}
        confirmDisabled={dependencyWarningHasRules}
      />
    </Box>
  );
};

export default FieldListBuilder;
